<template>
  <!-- Teleport to body so the drawer escapes any datatable/action-cell stacking
       context (e.g. the appointments report), where its z-index would otherwise
       be trapped beneath sticky action columns. -->
  <Teleport to="body">
    <div ref="wrapperRef">
      <Drawer
        include-header
        :no-footer="!appointment?.links?.cancel"
        :backdrop="backdrop"
        @hide-drawer="handleDrawerClosed"
        @show-drawer="handleDrawerOpened"
      >
        <template #drawerHeader>
          <a
            v-if="appointment?.job"
            class="w-full h-[48px] pt-[10px] drawer-header px-4 cursor-pointer text-white bg-primary-600 text-[13px] no-underline block"
            :href="jobShowPath(appointment.job.id)"
          >
            <div class="relative flex items-center justify-between w-full">
              <div class="underline">
                Job #{{ appointment.job.id }}
                {{
                  appointment.job.status
                    ? `(${appointment.job.status.name})`
                    : ""
                }}
              </div>
              <div class="flex items-center gap-1">
                View Job <ArrowRight class="w-4" />
              </div>
            </div>
          </a>
        </template>
        <template #title>
          <div id="status-dropdown-target"></div>
        </template>
        <AppointmentDetailsContent
          :appointment-id="appointmentId"
          @appointment-change="updateAppointment"
        />
        <template #footer>
          <Button
            label="Cancel Appointment"
            type="Alert"
            outline
            class="w-full"
            @click="toggleCancelDrawer"
          />
        </template>
      </Drawer>
      <CancelAppointmentDrawer
        v-if="cancelDrawer"
        :appointment="appointment"
        @hide-drawer="toggleCancelDrawer"
        @refresh-appointment="handleAppointmentCanceled"
      />
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onUnmounted, ref } from "vue";
import { EventBus } from "@/lib/event_bus.js";
import { removeStrandedNode } from "@/lib/removeStrandedNode";
import { updateQueryParam } from "@/lib/url";

import Button from "@/components/Seranova/Buttons/Button.vue";
import Drawer from "@/components/Seranova/Drawer/Drawer.vue";
import ArrowRight from "@/images/ArrowRight.svg?component";
import { jobShowPath } from "@/mixins/path_helpers";
import { AppointmentV2 } from "@/typings/appointment";
import AppointmentDetailsContent from "./AppointmentDetailsContent.vue";
import CancelAppointmentDrawer from "./CancelAppointmentDrawer.vue";
const emit = defineEmits(["hide-drawer", "show-drawer", "appointment-updated"]);
const props = withDefaults(
  defineProps<{
    appointmentId: string | number;
    backdrop?: boolean;
    initialCancel?: boolean;
  }>(),
  { backdrop: true, initialCancel: false }
);
const appointment = ref<AppointmentV2 | null>(null);
const cancelDrawer = ref(false);
const autoOpenedCancel = ref(false);
const wrapperRef = ref<HTMLElement | null>(null);
// Captured before Vue clears the template ref during teardown.
let teleportedWrapper: HTMLElement | null = null;

// Safety net for stranded teleported overlays (TOF-617). Vue's Teleport teardown
// intermittently leaves this wrapper in <body> when the schedule-board drawer
// closes, stranding the drawer on screen. By onUnmounted, Vue has already
// attempted the removal, so remove the node ourselves only if it was left
// behind. removeStrandedNode is guarded by isConnected, so it never races Vue's
// own successful removal.
onBeforeUnmount(() => {
  teleportedWrapper = wrapperRef.value;
});
onUnmounted(() => {
  removeStrandedNode(teleportedWrapper);
  teleportedWrapper = null;
});

const updateAppointment = (data: AppointmentV2) => {
  // Only update events if the appointment already exists
  if (appointment.value) {
    refreshTimelineEvents();
  }
  appointment.value = data;
  // When opened straight into cancel mode, reveal the cancel drawer once the
  // appointment (and its cancel link) has loaded.
  if (props.initialCancel && !autoOpenedCancel.value && data?.links?.cancel) {
    autoOpenedCancel.value = true;
    cancelDrawer.value = true;
  }
  emit("appointment-updated", appointment.value);
};
const refreshTimelineEvents = () => {
  EventBus.$emit("refresh-schedule-board");
};
const toggleCancelDrawer = () => {
  cancelDrawer.value = !cancelDrawer.value;
};
const handleAppointmentCanceled = () => {
  refreshTimelineEvents();
  toggleCancelDrawer();
  emit("hide-drawer");
};

const handleDrawerOpened = () => {
  updateQueryParam("appointment_id", props.appointmentId.toString());
  emit("show-drawer");
};
const handleDrawerClosed = () => {
  emit("hide-drawer");
  updateQueryParam("appointment_id", null);
};
</script>

<style lang="scss"></style>
